import type { CallBackProps } from 'react-joyride';
import { Box, Link } from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import Joyride, { ACTIONS, EVENTS, LIFECYCLE } from 'react-joyride';
import type { FirstUseTourStepTooltipPlacement } from '@common/utils/firstUseTourStepTooltipPlacement';
import { Breakpoints } from '@common/enums/breakpoints';
import { useViewportWidth } from '@frontend/utils/hooks';
import { useUiStateContext } from '@frontend/utils/hooks/useUiState';
import { translateKey } from '../../../translate';
import { useAbortableFetch } from '../../../utils/hooks/useAbortableFetch';

const firstUseTourTextColor = '#FFF';

interface CustomWindow extends Window {
    __webpack_nonce__: string;
}

interface IFirstUseTourStep {
    completed: boolean;
    content: string | null;
    id: string;
    image: string | null;
    link?: string;
    linkText?: string;
    placement: FirstUseTourStepTooltipPlacement | null;
    target: string;
    title: string;
}

interface IFirstUseTourStepsProps {
    url: string;
}

export function FirstUseTour(props: IFirstUseTourStepsProps) {
    const abortableFetch = useAbortableFetch();
    const { width } = useViewportWidth();
    const isMobileOrTablet = width < Breakpoints.large;
    const { uiState } = useUiStateContext();
    const [stepIndex, setStepIndex] = useState<number>(0);
    const [run, setRun] = useState<boolean>(false);
    const [steps, setSteps] = useState<IFirstUseTourStep[]>([]);

    useEffect(() => {
        loadSteps();
    }, []);

    async function loadSteps() {
        const result = await abortableFetch.getJson(`/api/firstUseTourSteps/currentPractitioner?url=${props.url}`);

        if (result.json?.firstUseTour) {
            if (result.json.firstUseTour.steps.length > 0) {
                const visibleSteps = result.json.firstUseTour.steps.filter(
                    (item: IFirstUseTourStep) => document.querySelector(item.target) !== null
                );
                if (visibleSteps.length > 0) {
                    setStepIndex(0);
                    setSteps(visibleSteps);
                    setRun(true);
                }
            }
        }
    }

    function handleJoyrideCallback(data: CallBackProps) {
        if (data.type === EVENTS.TARGET_NOT_FOUND) {
            handleStepChange(data);
        } else if (data.type === EVENTS.STEP_AFTER && data.lifecycle === LIFECYCLE.COMPLETE) {
            if (data.action === ACTIONS.NEXT) {
                handleStepChange(data, true);
            } else if (data.action === ACTIONS.PREV) {
                handleStepChange(data);
            } else if (data.action === ACTIONS.CLOSE) {
                setRun(false);
            }
        } else if (
            data.lifecycle === LIFECYCLE.TOOLTIP &&
            stepIndex === steps.length - 1 &&
            !steps[stepIndex].completed
        ) {
            handleStepChange(data, true);
        }
    }

    async function handleStepChange(data: CallBackProps, completeStep?: boolean) {
        let increment = 0;
        if (data.action === ACTIONS.PREV) {
            increment = -1;
        } else if (data.action === ACTIONS.NEXT) {
            increment = 1;
        }
        setStepIndex(data.index + increment);
        const step = steps[data.index];
        if (completeStep && !step.completed) {
            await abortableFetch.fetchJson(`/api/firstUseTourSteps/${steps[data.index].id}/complete`, 'POST');
            step.completed = true;
            setSteps(steps);
        }
    }

    function buildSteps() {
        return steps.map((step) => {
            const content = (
                <div>
                    {renderStepContent(step)}
                    {renderStepImage(step)}
                    {renderStepLink(step)}
                </div>
            );

            return {
                content,
                disableBeacon: true,
                placement: step.placement || undefined,
                target: step.target,
                title: <span className="white-title">{step.title}</span>,
            };
        });
    }

    function renderStepContent(step: IFirstUseTourStep) {
        return <span className="white-body">{step.content || ''}</span>;
    }

    function renderStepImage(step: IFirstUseTourStep) {
        if (step.image) {
            return (
                <img
                    alt="step"
                    src={step.image}
                ></img>
            );
        }

        return null;
    }

    function renderStepLink(step: IFirstUseTourStep) {
        if (step.link) {
            return (
                <Box mt={8}>
                    <Link
                        iconLeft="external"
                        onClick={(e) => {
                            e.preventDefault();
                            window.open(step.link, '_blank');
                        }}
                        sx={{ color: firstUseTourTextColor }}
                        variant="externalLink"
                    >
                        {step.linkText ?? step.link}
                    </Link>
                </Box>
            );
        }

        return null;
    }

    if (!run || steps.length === 0 || uiState.isAskEmploymentForSpecializationsModalOpen || isMobileOrTablet) {
        return null;
    }

    return (
        <>
            <Joyride
                callback={(data) => handleJoyrideCallback(data)}
                continuous
                disableOverlay
                disableOverlayClose
                disableScrolling
                disableScrollParentFix
                locale={{
                    back: translateKey('common.back'),
                    last: translateKey('common.last'),
                    next: translateKey('common.next'),
                }}
                nonce={(window as unknown as CustomWindow).__webpack_nonce__}
                run={run}
                showProgress
                stepIndex={stepIndex}
                steps={buildSteps()}
                styles={{
                    beacon: {
                        display: 'none',
                    },
                    options: {
                        arrowColor: '#00ADF8',
                        backgroundColor: '#00ADF8',
                        primaryColor: '#00ADF8',
                        textColor: firstUseTourTextColor,
                        zIndex: 1000,
                    },
                    tooltipContainer: {
                        textAlign: 'left',
                    },
                }}
            />
        </>
    );
}
